import { useState, useEffect } from "react";
import {
  FormControlLabel,
  MenuItem,
  Select,
  Checkbox,
  Typography,
  FormControl,
  Button,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import { useMapState } from "contexts/MapStateContext";
import { Loader } from "../UiComponents";
import PlaybackControl from "./PlaybackControl";
import SuitabilityControl from "./SuitabilityControl";
import useRun from "hooks/useRun";
import { downloadAsFormat, filterPathwaysByRunType } from "utils/exportData";
import {
  EXPORT_FORMATS_NO_SHP,
  VEGETATION_DOWNLOAD_LAYERS,
} from "constants/exportData";
import { downloadFromUrl } from "utils/file";
import { formatDate } from "utils";

function formatVersion(version) {
  const labels = {
    baseline: "Baseline",
    override: "Hypothetical",
  };
  return labels[version];
}

const defaultFormat = "geojson";

// Resolve a raster layer's download URL from the run. Hypothetical rasters are
// nested under `hypothetical_files`; all other rasters sit at the run's top level.
function getRasterUrl(currRun, layer) {
  if (!currRun || !layer) return undefined;
  if (layer.source === "hypothetical") {
    return currRun?.hypothetical_files?.[layer.runKey];
  }
  return currRun?.[layer.runKey];
}

export default function VegetationMapControl() {
  const {
    overlay,
    setOverlay,
    pathwaysVersion,
    setPathwaysVersion,
    allowedVersions,
    showEntryPoints,
    setShowEntryPoints,
    showOverrides,
    setShowOverrides,
  } = useMapState();
  const { currRun, isRunLoading } = useRun();
  const [downloadLayer, setDownloadLayer] = useState("pathways_all");
  const [downloadFormat, setDownloadFormat] = useState(defaultFormat);
  const [isExporting, setIsExporting] = useState(false);

  const nameBase = currRun?.name?.replace(/[^\w]/g, "") || "run";
  const dateStr = formatDate(new Date());

  const availableLayers = VEGETATION_DOWNLOAD_LAYERS.filter((opt) => {
    if (opt.type === "pathway") return !!currRun?.pathways?.features?.length;
    if (opt.type === "vector") {
      const data = currRun?.[opt.runKey];
      return data?.features?.length > 0;
    }
    if (opt.type === "raster") {
      const url = getRasterUrl(currRun, opt);
      return url != null && url !== "";
    }
    return false;
  });

  useEffect(() => {
    if (!currRun) return;
    const available = VEGETATION_DOWNLOAD_LAYERS.filter((opt) => {
      if (opt.type === "pathway") return !!currRun?.pathways?.features?.length;
      if (opt.type === "vector")
        return currRun?.[opt.runKey]?.features?.length > 0;
      if (opt.type === "raster") {
        const url = getRasterUrl(currRun, opt);
        return url != null && url !== "";
      }
      return false;
    });
    const currentAvailable = available.some((l) => l.value === downloadLayer);
    if (!currentAvailable && available.length > 0) {
      setDownloadLayer(available[0].value);
    }
  }, [currRun, downloadLayer]);

  const selectedLayerConfig =
    VEGETATION_DOWNLOAD_LAYERS.find((l) => l.value === downloadLayer) ||
    availableLayers[0];
  const isPathway = selectedLayerConfig?.type === "pathway";
  const isVector = selectedLayerConfig?.type === "vector";
  const isRaster = selectedLayerConfig?.type === "raster";

  const canDownload =
    availableLayers.some((l) => l.value === downloadLayer) &&
    (isPathway
      ? currRun?.pathways?.features?.length
      : isVector
        ? currRun?.[selectedLayerConfig?.runKey]?.features?.length > 0
        : isRaster && getRasterUrl(currRun, selectedLayerConfig));

  const handleDownloadData = async () => {
    if (!selectedLayerConfig || !currRun) return;
    setIsExporting(true);
    try {
      if (selectedLayerConfig.type === "pathway") {
        const filtered = filterPathwaysByRunType(
          currRun.pathways,
          selectedLayerConfig.subset
        );
        const filenameBase = `xyloplan_${nameBase}_pathways_${selectedLayerConfig.subset}`;
        await downloadAsFormat(filtered, filenameBase, downloadFormat);
      } else if (selectedLayerConfig.type === "vector") {
        const data = currRun[selectedLayerConfig.runKey];
        const slug = selectedLayerConfig.runKey;
        const filenameBase = `xyloplan_${nameBase}_${slug}`;
        await downloadAsFormat(data, filenameBase, downloadFormat);
      } else if (selectedLayerConfig.type === "raster") {
        const url = getRasterUrl(currRun, selectedLayerConfig);
        if (!url) return;
        const ext = url.split(".").pop()?.split("?")[0] || "tif";
        const filename = `xyloplan_${nameBase}_${selectedLayerConfig.runKey}_${dateStr}.${ext}`;
        await downloadFromUrl(url, filename);
      }
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      {isRunLoading && <Loader size={50} sx={{ mt: 2 }} />}
      {!isRunLoading && currRun && (
        <>
          <PlaybackControl
            maxAnimationTime={currRun?.config?.simulation?.duration}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showEntryPoints}
                onChange={(e) => setShowEntryPoints(e.target.checked)}
                color="primary"
              />
            }
            label="Entry Points"
            labelPlacement="start"
            sx={{
              margin: 0,
              width: "100%",
              justifyContent: "space-between",
            }}
          />
          {currRun?.overrides?.features?.length > 0 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={showOverrides}
                  onChange={(e) => setShowOverrides(e.target.checked)}
                  color="primary"
                />
              }
              label="Overrides"
              labelPlacement="start"
              sx={{
                margin: 0,
                width: "100%",
                justifyContent: "space-between",
              }}
            />
          )}

          <Select
            variant="standard"
            labelId="select-overlay-label"
            id="select-overlay"
            value={pathwaysVersion}
            onChange={(event) => setPathwaysVersion(event.target.value)}
            fullWidth
            sx={{ my: 1 }}
          >
            {allowedVersions.map((version) => {
              return (
                <MenuItem key={version} value={version}>
                  {formatVersion(version)}
                </MenuItem>
              );
            })}
          </Select>
          <Select
            variant="standard"
            labelId="select-overlay-label"
            id="select-overlay"
            fullWidth
            value={overlay}
            onChange={(event) => setOverlay(event.target.value)}
            sx={{ my: 1 }}
          >
            <MenuItem value="default">No Map Overlays</MenuItem>
            <MenuItem value="wind_speed">Wind Speed</MenuItem>
            <MenuItem value="wind_direction">Wind Direction</MenuItem>
            <MenuItem value="arrival_time">Arrival Time</MenuItem>
            <MenuItem value="rate_of_spread">Rate of Spread</MenuItem>
            <MenuItem value="flame_length">Flame Length</MenuItem>
            {currRun?.hypothetical_files?.hypothetical_arrival_time && [
              <MenuItem
                key="hypothetical_arrival_time"
                value="hypothetical_arrival_time"
              >
                Hypothetical Arrival Time
              </MenuItem>,
              <MenuItem
                key="hypothetical_rate_of_spread"
                value="hypothetical_rate_of_spread"
              >
                Hypothetical Rate of Spread
              </MenuItem>,
              <MenuItem
                key="hypothetical_flame_length"
                value="hypothetical_flame_length"
              >
                Hypothetical Flame Length
              </MenuItem>,
            ]}
            <MenuItem value="arrival_delta">Change in Arrival Time</MenuItem>
            <MenuItem value="osm_mask">Added Nonburnable Features</MenuItem>
          </Select>
          {currRun?.suitability?.features?.length > 0 && <SuitabilityControl />}

          <Typography variant="h6" sx={{ mt: 2, mb: 1.5 }}>
            Download Data
          </Typography>
          <FormControl fullWidth variant="standard" sx={{ mb: 1.5 }}>
            <Select
              value={
                availableLayers.some((l) => l.value === downloadLayer)
                  ? downloadLayer
                  : availableLayers[0]?.value ?? ""
              }
              onChange={(e) => setDownloadLayer(e.target.value)}
              displayEmpty
            >
              {availableLayers.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {(isPathway || isVector) && (
            <FormControl fullWidth variant="standard" sx={{ mb: 1.5 }}>
              <Select
                value={downloadFormat}
                onChange={(e) => setDownloadFormat(e.target.value)}
                displayEmpty
              >
                {EXPORT_FORMATS_NO_SHP.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileDownloadOutlinedIcon />}
            onClick={handleDownloadData}
            disabled={!canDownload || isExporting}
            fullWidth
            sx={{ mt: 1 }}
          >
            {isExporting ? "Exporting..." : "Download"}
          </Button>
        </>
      )}
    </>
  );
}
